import React, { useCallback, useEffect, useState } from 'react';
import { api, errText } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { LifeBuoy, Loader2, Send } from 'lucide-react';

const statusStyle = {
  open: 'bg-amber-500/10 text-amber-600 border-amber-500/40',
  in_progress: 'bg-primary/10 text-primary border-primary/30',
  resolved: '',
};

function Ticket({ t, onChanged }) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const update = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/owner/tickets/${t.id}`, payload);
      toast.success('Ticket updated');
      setReply('');
      onChanged?.();
    } catch (e) {
      toast.error(errText(e, 'Could not update ticket'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid={`owner-ticket-${t.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium">{t.subject}</p>
            <p className="text-xs text-muted-foreground">
              {t.created_by?.name} ({t.created_by?.role}) · {t.practice_name || 'Platform'} · {(t.created_at || '').slice(0, 10)}
            </p>
          </div>
          <div className="flex gap-1.5">
            <Badge variant="secondary" className="text-[10px] capitalize">{t.source}</Badge>
            <Badge variant="outline" className={`text-[10px] capitalize ${statusStyle[t.status] || ''}`}>{String(t.status).replace('_', ' ')}</Badge>
          </div>
        </div>
        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{t.message}</p>
        {(t.replies || []).map((r, i) => (
          <div key={i} className="rounded-lg bg-secondary/60 border px-3 py-2 text-sm">
            <p className="text-[11px] text-muted-foreground mb-0.5">{r.name} · {(r.at || '').slice(0, 16).replace('T', ' ')}</p>
            {r.message}
          </div>
        ))}
        <div className="flex flex-col sm:flex-row gap-2">
          <Textarea rows={1} placeholder="Reply to this ticket…" value={reply} onChange={(e) => setReply(e.target.value)} className="flex-1 min-h-[38px]" data-testid={`owner-ticket-reply-input-${t.id}`} />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !reply.trim()} onClick={() => update({ reply, status: t.status === 'open' ? 'in_progress' : t.status })} data-testid={`owner-ticket-reply-send-${t.id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Reply
            </Button>
            {t.status !== 'resolved' ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => update({ status: 'resolved' })} data-testid={`owner-ticket-resolve-${t.id}`}>Resolve</Button>
            ) : (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => update({ status: 'open' })} data-testid={`owner-ticket-reopen-${t.id}`}>Reopen</Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OwnerSupport() {
  const [status, setStatus] = useState('all');
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    api.get('/owner/tickets', { params: { status } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4" data-testid="owner-support-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Support desk</h1>
        <p className="text-sm text-muted-foreground">Help requests from patients, staff and doctors across every practice.</p>
      </div>
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList data-testid="owner-support-filter">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="in_progress">In progress</TabsTrigger>
          <TabsTrigger value="resolved">Resolved</TabsTrigger>
        </TabsList>
      </Tabs>
      {!items ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="owner-support-empty">
          <LifeBuoy className="h-8 w-8 mx-auto mb-2 opacity-40" /> No tickets here — inbox zero.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">{items.map((t) => <Ticket key={t.id} t={t} onChanged={load} />)}</div>
      )}
    </div>
  );
}
